/**
 * AP-30-A — Der Rechtekatalog.
 *
 * **Die Rechte selbst stehen nicht hier.** Sie stehen an ihrem Modul in
 * `modules.ts`, so wie ein Modul auch seinen Schlüssel mitbringt. Diese Datei
 * leitet daraus das ab, was der Rest des Systems braucht: den Typ `Permission`,
 * die Zuordnung Recht → Registry-Eintrag und die Regeln des Geltungsbereichs.
 *
 * Konzept: `docs/Arbeitspakete/AP-30/AP-30_Berechtigungen-und-Mandanten-Rollen.md`
 */

import {
  MODULE_REGISTRY,
  getModule,
  type ModuleDefinition,
  type ModuleKey,
  type Permission,
} from './modules'

export type { Permission }

/** Alle Rechte in Registry-Reihenfolge, ohne Doppelte. */
export const ALL_PERMISSIONS: readonly Permission[] = MODULE_REGISTRY.flatMap(
  (m) => m.permissions as readonly Permission[],
)

// ─── Geltungsbereich ────────────────────────────────────────────────────────

/**
 * Die beiden Geltungsbereiche. **Sie sind Teil des Rechts, nicht des Codes**
 * (AP-30 4.1): `time.entry.read.own` und `time.entry.read.all` sind zwei
 * verschiedene Rechte, und die Ownership-Prüfung im Service bleibt bestehen —
 * sie wird nur nicht mehr von der Rolle gesteuert, sondern von hier.
 *
 * *Ein Recht ohne Geltungsbereich zu vergeben, ist der teuerste Fehler in
 * diesem Paket*: `time.entry.read` allein sagt nicht, **wessen** Buchungen.
 */
export type Scope = 'own' | 'all'

/**
 * Die **Grundformen** mit Geltungsbereich: jedes `x`, zu dem es sowohl `x.own`
 * als auch `x.all` gibt.
 *
 * Abgeleitet, nicht gepflegt. Wer ein Paar anlegt, bekommt den Eintrag hier
 * geschenkt; wer nur eine Hälfte anlegt, fällt im Test auf — eine einsame
 * `.own`-Hälfte wäre ein Recht, das niemand je auf „alle" erweitern kann.
 */
export const SCOPED_BASES: readonly string[] = [
  ...new Set(
    ALL_PERMISSIONS.filter((p) => p.endsWith('.own'))
      .map((p) => p.slice(0, -'.own'.length))
      .filter((basis) => (ALL_PERMISSIONS as readonly string[]).includes(`${basis}.all`)),
  ),
]

export function isScopedBase(kandidat: string): boolean {
  return SCOPED_BASES.includes(kandidat)
}

/**
 * Was an einem Endpunkt verlangt werden darf: ein Recht **oder** eine
 * Grundform mit Geltungsbereich.
 *
 * `@RequirePermission('time.entry.read')` heißt „mit eigenem *oder* mit vollem
 * Geltungsbereich" — welcher davon, entscheidet der Service beim Filtern. Wer
 * `time.entry.read.all` schreibt, verlangt ausdrücklich den vollen.
 */
export type PermissionRequirement = Permission | (string & {})

/**
 * Erfüllt diese Rechteliste die Anforderung?
 *
 * Die einzige Stelle, an der „darf er das?" beantwortet wird — Guard, Frontend
 * und PWA rufen dieselbe Funktion. Ein zweiter Rechenweg wäre die erste
 * Abweichung zwischen „die Schaltfläche ist da" und „der Endpunkt lässt durch".
 */
export function erfuelltRecht(
  verlangt: string,
  gehalten: readonly string[] | undefined,
): boolean {
  // Eine fehlende Liste ist **keine** volle Liste. Der Fall entsteht an einem
  // alten Token und in jedem Testaufbau, der `permissions` nicht setzt — und er
  // muss zu „nein" führen, nicht zu einem Absturz und erst recht nicht zu „ja".
  if (!gehalten) return false
  if (gehalten.includes(verlangt)) return true
  // Grundform: `.own` genügt für den Zutritt, der Service schneidet die Daten
  // zu. Ohne diese Zeile bräuchte jeder Endpunkt zwei Dekoratoren.
  if (isScopedBase(verlangt)) {
    return gehalten.includes(`${verlangt}.own`) || gehalten.includes(`${verlangt}.all`)
  }
  return false
}

/**
 * Der Geltungsbereich, in dem dieser Benutzer eine Grundform hält — `null`,
 * wenn gar nicht.
 *
 * `'all'` schlägt `'own'`: Rechte addieren sich (AP-30, Abschnitt 3), also
 * gewinnt die weitere Sicht, wenn eine zweite Rolle sie mitbringt.
 */
export function geltungsbereich(
  basis: string,
  gehalten: readonly string[] | undefined,
): Scope | null {
  if (gehalten?.includes(`${basis}.all`)) return 'all'
  if (gehalten?.includes(`${basis}.own`)) return 'own'
  return null
}

/** Kurzform für den häufigsten Fall: „sieht er nur seine eigenen?" */
export function nurEigene(basis: string, gehalten: readonly string[] | undefined): boolean {
  return geltungsbereich(basis, gehalten) !== 'all'
}

// ─── Zuordnung Recht → Registry-Eintrag ─────────────────────────────────────

/**
 * Die Abbildung, die der Schnitt aus AP-30 5.1 braucht — **aus der Deklaration,
 * nie aus dem Namen** (AP-30 3a.2).
 *
 * Das Recht heißt `time.entry.read.own`, sein Eintrag heißt
 * `TIME_TRACKING.CORE`. Wer stattdessen den Präfix parst, hat eine zweite
 * Wahrheit gebaut: AP-29-N-B hat die Zugehörigkeit eines Bausteins schon einmal
 * als `startsWith` auf dem Punkt im Schlüssel geführt, bevor sie die Registry
 * gelesen hat.
 */
const EINTRAG_JE_RECHT: ReadonlyMap<string, ModuleKey> = new Map(
  MODULE_REGISTRY.flatMap((m) => m.permissions.map((p) => [p as string, m.key] as const)),
)

/** Zu welchem Registry-Eintrag gehört dieses Recht? */
export function moduleOfPermission(permission: string): ModuleKey | undefined {
  // Eine Grundform (`time.entry.read`) steht selbst nicht im Katalog — ihre
  // Hälften schon, und beide gehören zwangsläufig zum selben Eintrag (Test).
  return (
    EINTRAG_JE_RECHT.get(permission) ??
    EINTRAG_JE_RECHT.get(`${permission}.own`) ??
    EINTRAG_JE_RECHT.get(`${permission}.all`)
  )
}

export function isPermission(kandidat: string): kandidat is Permission {
  return EINTRAG_JE_RECHT.has(kandidat)
}

/** Die Rechte eines Registry-Eintrags. Leer für einen Eintrag ohne eigene. */
export function permissionsOfModule(key: string): readonly Permission[] {
  return (getModule(key)?.permissions ?? []) as readonly Permission[]
}

/**
 * Die Einträge, die **Rechte mitbringen** — die Gruppierung der Häkchenliste in
 * der Rollenverwaltung (AP-30-E).
 *
 * Ein Eintrag ohne Rechte (eine Gruppe, ein quer schneidender Baustein, ein
 * geplantes Modul) taucht dort nicht auf: Eine leere Überschrift ist eine
 * Frage, die der Kunde nicht beantworten kann.
 */
export function modulesWithPermissions(): ModuleDefinition[] {
  return MODULE_REGISTRY.filter((m) => m.permissions.length > 0)
}

// ─── Der Schnitt aus AP-30 5.1 ──────────────────────────────────────────────

/**
 * ```
 * rechte(U) := ⋃ { rolle.permissions | rolle ∈ Rollen(U) }
 *              ∩ { Rechte der Module, in denen U einen Platz hat }
 * ```
 *
 * **Die Vereinigung steht woanders** (`PermissionService`, weil sie die
 * Datenbank braucht); hier steht der Schnitt, weil er eine reine Regel ist und
 * auch das Frontend ihn nachvollziehen können muss.
 *
 * `alwaysActive`-Einträge überstehen ihn immer — sonst würde `CORE` jedes Mal
 * weggeschnitten, und nach der Umstellung könnte niemand mehr Benutzer anlegen.
 *
 * Ein Recht, dessen Modul die Registry nicht kennt, fällt heraus. Das ist der
 * Fall „Recht umbenannt, alte Rolle steht noch in der Datenbank": Es gehört zu
 * keinem Code mehr und kann nichts freischalten.
 */
export function schneideMitModulen(
  rechte: readonly string[] | undefined,
  aktiveModule: readonly string[] | undefined,
): string[] {
  const aktiv = new Set(aktiveModule ?? [])
  return [...new Set(rechte ?? [])].filter((p) => {
    const key = moduleOfPermission(p)
    if (!key) return false
    return getModule(key)?.alwaysActive === true || aktiv.has(key)
  })
}
